import { config } from '@client/config';
import { B2BRegistrationSubmitData } from '../api/B2BRegistration.types';
import { FocusFieldType } from './constants';

const SALUTATION_DE: Record<string, string> = {
  male: 'Herr',
  female: 'Frau',
  none: 'Keine Angabe',
};

const BUSINESS_TYPE_DE: Record<string, string> = {
  physicalStore: 'Ladengeschäft(e)',
  onlineShop: 'Online-Shop',
  solutionProvider: 'Lösungsanbieter',
  carrierServiceProvider: 'Carrier/Service Provider',
  other: 'Sonstiges',
};

const CONTACT_ROLE_DE: Record<string, string> = {
  managingDirector: 'Geschäftsleiter/-in',
  headOfPurchasing: 'Leiter/-in Einkauf',
  headOfSales: 'Leiter/-in Verkauf',
  headOfEngineering: 'Leiter/-in Technik',
  headOfService: 'Leiter/-in Service',
  headOfMarketing: 'Leiter/-in Marketing',
  projectManager: 'Projektleiter/-in',
  buyer: 'Einkäufer/-in',
  salesperson: 'Verkäufer/-in',
  administration: 'Administration',
  other: 'Sonstiges',
};

const PLANNED_REVENUE_DE: Record<string, string> = {
  lessThan10k: 'Weniger als CHF 10\'000',
  '10kTo25k': 'CHF 10\'000 bis 25\'000',
  '25kTo50k': 'CHF 25\'000 bis 50\'000',
  '50kTo100k': 'CHF 50\'000 bis 100\'000',
  '100kTo250k': 'CHF 100\'000 bis 250\'000',
  moreThan250k: 'Mehr als CHF 250\'000',
};

const FOCUS_FIELD_DE: Record<FocusFieldType, string> = {
  computingCloud: 'Computing & Cloud',
  networkSecurity: 'Netzwerk & Sicherheit',
  telcoUcc: 'Telco & UCC',
  proAvMultimedia: 'Pro AV & Multimedia',
  electroHousehold: 'Elektro & Haushalt',
  officeLeisure: 'Büro & Freizeit',
  pcNotebooks: 'PC/Notebooks',
  peripherals: 'Peripherie/Komponenten',
  software: 'Software',
  cloudServices: 'Cloud/Services',
  network: 'Netzwerk',
  serverDatacenter: 'Server/Datacenter',
  itSecurity: 'IT Security',
  videoSurveillance: 'Videoüberwachung/Physical Security',
  telephoneSystems: 'Telefonsysteme',
  conferencingWorkSmart: 'Conferencing/Work Smart',
  mobileTelephone: 'Mobile-/Telefone',
  proAvProjection: 'Pro AV/Projektion',
  tvAudio: 'TV/Audio',
  photoVideo: 'Foto/Video',
  buildingTechnology: 'Gebäudetechnik',
  electroLighting: 'Elektro/Beleuchtung',
  electromobility: 'Elektromobilität',
  solarManagement: 'Solarmanagement',
  householdDevices: 'Haushaltsgeräte',
  officeSupplies: 'Bürobedarf',
  toolsDoItGarden: 'Werkzeug/Do-it & Garten',
  leisure: 'Freizeit',
  gaming: 'Gaming',
  rcModelBuilding: 'RC-Modellbau',
  supermarketDrugstore: 'Supermarkt/Drogerie',
  various: 'Diverses',
};

const NEWSLETTER_DE: Record<string, string> = {
  newsletterIT: 'IT-News (wöchentlich)',
  newsletterElektro: 'Elektro-News (wöchentlich)',
  newsletterTelco: 'Telco-News (wöchentlich)',
  newsletterProAV: 'Pro AV-News (wöchentlich)',
  newsletterSecurity: 'Security-News (wöchentlich)',
};

const formatFocusFields = (focusFields: FocusFieldType[]): string => {
  const checkedLabels: string[] = [];

  focusFields.forEach((field) => {
    const label = FOCUS_FIELD_DE[field];
    if (label) {
      checkedLabels.push(label);
    }
  });

  return checkedLabels.length > 0 ? `[${checkedLabels.join(', ')}]` : '[]';
};

const formatNewsletters = (data: B2BRegistrationSubmitData): string => {
  const newsletters: string[] = [];

  if (data.newsletterIT) {
    newsletters.push(NEWSLETTER_DE.newsletterIT);
  }
  if (data.newsletterElektro) {
    newsletters.push(NEWSLETTER_DE.newsletterElektro);
  }
  if (data.newsletterTelco) {
    newsletters.push(NEWSLETTER_DE.newsletterTelco);
  }
  if (data.newsletterProAV) {
    newsletters.push(NEWSLETTER_DE.newsletterProAV);
  }
  if (data.newsletterSecurity) {
    newsletters.push(NEWSLETTER_DE.newsletterSecurity);
  }

  return newsletters.length > 0 ? `[${newsletters.join(', ')}]` : '[]';
};

export const formatFormDataAsString = (data: B2BRegistrationSubmitData): string => {
  const lines: string[] = [];

  lines.push('');
  lines.push('----------');
  lines.push('');

  const addField = (label: string, value: string | undefined | null) => {
    lines.push(`${label}: ${value || ''}`);
  };

  const fullAddress = [data.street, data.streetNumber].filter(Boolean).join(' ');

  const contactName = [data.contactFirstName, data.contactLastName].filter(Boolean).join(' ');

  const applicantDeviating = data.applicantSameAsContact === 'newContact' ? 'Ja' : 'Nein';

  const applicantName = [data.applicantFirstName, data.applicantLastName].filter(Boolean).join(' ');

  const businessTypes = Array.isArray(data.businessTypes)
    ? data.businessTypes.map((type) => BUSINESS_TYPE_DE[type] || type)
    : [];
  const businessTypesFormatted = businessTypes.length > 0
    ? `[${businessTypes.join(', ')}]`
    : '[]';

  addField('Firmenname', data.companyName);
  addField('FirmennameZusatz', data.companyNameAddition);
  addField('Adresse', fullAddress);
  addField('PLZ', data.zipCode);
  addField('Ort', data.city);
  addField('Telefon', data.contactPhone);
  addField('E-Mail-Adresse (allgemein)', data.companyEmail);
  addField('AnzahlMitarbeitende', data.employeeCount);
  addField('Website', data.websiteUrl);
  addField('UID-/Handelsregister', data.uid);
  addField('UID-Daten verwendet', data.uidPrefilledDataUsed ? 'Ja' : 'Nein');

  lines.push(`Geschäfte: ${businessTypesFormatted}`);

  addField('Jahresumsatz', data.plannedRevenue ? PLANNED_REVENUE_DE[data.plannedRevenue] : '');

  const focusFieldsList = Array.isArray(data.focusFields) ? data.focusFields : [];
  lines.push(`Schwerpunktbereiche: ${formatFocusFields(focusFieldsList)}`);

  addField('Partnerprogramme', data.partnerProgrammes);
  addField('Zertifizierungen', data.certifications);
  addField('Owner Name', contactName);
  addField('OwnerFunktion', data.contactRole ? CONTACT_ROLE_DE[data.contactRole] : '');
  addField('Owner E-Mail-Adresse', data.contactEmail);
  lines.push(`${config.shopName} Cloud Service Platform: ${data.cloudServiceInterest ? 'Ja' : 'Nein'}`);
  addField('Marktplatz Telefon', data.cloudServicePhone);

  lines.push(`Newsletter: ${formatNewsletters(data)}`);

  addField('Antragsteller Name', applicantName);
  addField('Antragsteller Funktion', data.applicantRole ? CONTACT_ROLE_DE[data.applicantRole] : '');
  addField('Antragsteller E-Mail-Adresse', data.applicantEmail);
  addField('Antragsteller Telefon', data.applicantPhone);

  lines.push('Bemerkungen:');

  lines.push('');
  addField('Rechtsform', data.legalForm);
  addField('Land', data.country);
  addField('Adresszusatz', data.addressAddition);
  addField('NOGA Nummer', data.nogaNumber);
  addField('Anzahl Büroarbeitsplätze', data.officeWorkplaces);
  addField('Kontaktperson Anrede', data.contactSalutation ? SALUTATION_DE[data.contactSalutation] : '');
  addField('Kontaktperson Telefon', data.contactPhone);
  addField('Antragsteller weicht vom Kontakt ab', applicantDeviating);

  lines.push('');
  lines.push('----------');
  lines.push('');

  return lines.join('\n');
};
